import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Avocado from "../../../assets/produce/avocado.png";
import Mandarins from "../../../assets/produce/mandarin.png";
import Grapes from "../../../assets/produce/grapes.png";
import Blueberries from "../../../assets/produce/blueberries.png";
import Mango from "../../../assets/produce/mango.png";
import Pomegranates from "../../../assets/produce/pomegranate.png";
import Cherry from "../../../assets/produce/cherry.png";
import Kiwi from "../../../assets/produce/kiwi.png";
import Pear from "../../../assets/produce/pear.png";
import Ginger from "../../../assets/produce/ginger.png";
import Lime from "../../../assets/produce/lime.png";
import Lemon from "../../../assets/produce/lemon.png";

// Define types for Product props
interface ProductProps {
  imageSrc: string;
  name: string;
  delay: number;
}

// Component to display each product
const Product: React.FC<ProductProps> = ({ imageSrc, name, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ 
        y: -4,
        transition: { duration: 0.3 }
      }}
      className="group flex flex-col items-center text-center p-3 md:p-4 lg:p-6 bg-white backdrop-blur-sm rounded-xl md:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-[#f84831]/10 hover:border-[#f84831]/20"
    >
      <div className="relative overflow-hidden rounded-lg md:rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 p-2 md:p-3 lg:p-4 mb-2 md:mb-3 lg:mb-4 group-hover:scale-105 transition-transform duration-300">
        <img src={imageSrc} alt={name} className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 object-contain" />
        <div className="absolute inset-0 bg-[#f84831]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg md:rounded-xl"></div>
      </div>
      <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-semibold text-[#f84831] group-hover:text-[#e63e26] transition-colors duration-300 leading-tight">
        {name}
      </h3>
    </motion.div>
  );
};

// Component to display the grid of products
const ProductGrid: React.FC = () => {
  const { t } = useTranslation();

  // Define array of product details (only using available assets)
  const products = [
    {
      id: 1,
      name: t('inspectSet.products.avocados'),
      imageSrc: Avocado,
    },
    {
      id: 2,
      name: t('inspectSet.products.mandarins'),
      imageSrc: Mandarins,
    },
    {
      id: 3,
      name: t('inspectSet.products.grapes'),
      imageSrc: Grapes,
    },
    {
      id: 4,
      name: t('inspectSet.products.blueberries'),
      imageSrc: Blueberries,
    },
    {
      id: 5,
      name: t('inspectSet.products.mangoes'),
      imageSrc: Mango,
    },
    {
      id: 6,
      name: t('inspectSet.products.pomegranates'),
      imageSrc: Pomegranates,
    },
    {
      id: 7,
      name: t('inspectSet.products.cherries'),
      imageSrc: Cherry,
    },
    {
      id: 8,
      name: t('inspectSet.products.kiwis'),
      imageSrc: Kiwi,
    },
    {
      id: 9,
      name: t('inspectSet.products.pears'),
      imageSrc: Pear,
    },
    {
      id: 10,
      name: t('inspectSet.products.ginger'),
      imageSrc: Ginger,
    },
    {
      id: 11,
      name: t('inspectSet.products.limes'),
      imageSrc: Lime,
    },
    {
      id: 12,
      name: t('inspectSet.products.lemons'),
      imageSrc: Lemon,
    }
  ];

  return (
    <div className="w-full py-16 md:py-20 bg-gradient-to-br from-white via-gray-50 to-white">
      <div>
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12 md:mb-16 px-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-[#f84831] leading-tight">
            {t('inspectSet.heading')}
          </h2>
          <div className="mx-auto h-1 w-16 md:w-24 bg-gradient-to-r from-[#f84831] to-[#ff6b54] mb-4 md:mb-6 rounded-full"></div>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Our expert inspectors ensure quality across a wide range of fresh produce
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="px-4 sm:px-6 lg:px-8 mx-auto max-w-7xl">
          <div className="bg-[#f84831]/5 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 border border-[#f84831]/10">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6">
              {products.map((product, index) => (
                <Product 
                  key={product.id} 
                  imageSrc={product.imageSrc} 
                  name={product.name}
                  delay={index * 0.05}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA Section */}
        <motion.div 
          className="text-center mt-12 md:mt-16 px-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6">
            Don't see your product? We inspect many more varieties.
          </p>
          <button className="bg-[#f84831] hover:bg-[#e63e26] text-white px-6 md:px-8 py-3 rounded-xl font-semibold transition-colors duration-300 shadow-lg text-sm md:text-base">
            Contact Us for More Products
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductGrid;
